/**
 * Seções apresentacionais do modal de plantão (GUPM-272).
 *
 * Só JSX: nenhuma decide nada — recebem estado e callbacks. O `ShiftHandoverModal`
 * segue dono do estado e das regras; aqui mora o layout, que era a maior parte
 * das 550 linhas do arquivo original (teto do `frontend.md` é 200).
 *
 * `mode` chega como o booleano `isAbertura` porque as duas telas compartilham o
 * mesmo esqueleto e diferem em pontos localizados — quem sai, quantos códigos,
 * e se a observação é obrigatória.
 */

import type { ChecklistItem, ChecklistResponse, GroupColor } from '@/domain/shift-handover/types';
import { isItemComplete } from '@/domain/shift-handover/checklist-rules';
import ChecklistItemRow from '../ChecklistItemRow';
import type { OperatorOption } from '@/services/datasources';
import { SEM_SUBSTITUTO } from '../constants';

const GROUP_COLOR_CLASSES: Record<GroupColor, { dot: string; text: string }> = {
  red: { dot: 'bg-red-500', text: 'text-red-300' },
  amber: { dot: 'bg-amber-400', text: 'text-amber-200' },
  green: { dot: 'bg-green-500', text: 'text-green-300' },
};

export function FieldBox({ label, children }: { label: React.ReactNode; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-wide text-jf-dim mb-1">{label}</label>
      {children}
    </div>
  );
}

export function CodeInput({
  value,
  onChange,
  ariaLabel,
}: {
  value: string;
  onChange: (v: string) => void;
  /** O <label> do FieldBox não está associado ao input (não há htmlFor/id), então
   *  sem isto o campo fica sem nome acessível — e sem como ser encontrado em teste. */
  ariaLabel: string;
}) {
  return (
    <input
      type="text"
      aria-label={ariaLabel}
      inputMode="numeric"
      autoComplete="one-time-code"
      maxLength={6}
      value={value}
      onChange={(e) => onChange(e.target.value.replace(/\D/g, '').slice(0, 6))}
      placeholder="000000"
      className="w-full rounded-md border border-jf-border bg-jf-surface px-3 py-2 text-center text-lg tracking-[0.4em] text-white outline-none focus:border-jf-yellow"
    />
  );
}

export function HandoverHeader({
  isAbertura,
  isEncerramento,
  onClose,
  fecharTravado = false,
}: {
  isAbertura: boolean;
  isEncerramento: boolean;
  onClose: () => void;
  /** Enviando: o × trava, como o "Fechar" do rodapé (C-91). */
  fecharTravado?: boolean;
}) {
  return (
    <header
      className={`flex items-start justify-between border-b border-jf-border p-4 ${
        isEncerramento ? 'bg-red-500/[0.07]' : 'bg-jf-yellow/[0.06]'
      }`}
    >
      <div>
        {/* O cabeçalho muda ao escolher "— Ninguém —": a consequência da ação
            passou a ser outra, e a tela não pode continuar dizendo "troca". */}
        <h2 className={`text-lg font-bold ${isEncerramento ? 'text-red-300' : 'text-jf-yellow'}`}>
          {isAbertura
            ? '📋 Abertura de Plantão'
            : isEncerramento
            ? '⏹ Encerrar Plantão'
            : '📋 Troca de Plantão'}
        </h2>
        <p className="text-xs text-jf-muted">
          {isAbertura
            ? 'Ninguém está de plantão — você assume a vigilância das barragens A, B e C'
            : isEncerramento
            ? 'Ninguém vai render você — a operação fica sem plantonista até alguém abrir plantão'
            : 'Verifique todos os itens antes de assumir o turno'}
        </p>
      </div>
      <button
        onClick={onClose}
        disabled={fecharTravado}
        className="text-2xl leading-none text-jf-muted hover:text-white disabled:opacity-50"
        aria-label="Fechar"
      >
        ×
      </button>
    </header>
  );
}

export function HandoverParticipantes({
  isAbertura,
  myName,
  operatorInId,
  onOperatorInChange,
  operators,
  operatorsLoading,
  submitAttempted,
}: {
  isAbertura: boolean;
  myName: string;
  operatorInId: string;
  onOperatorInChange: (id: string) => void;
  operators: OperatorOption[];
  operatorsLoading: boolean;
  submitAttempted: boolean;
}) {
  return (
    <section className="border-b border-jf-border bg-jf-surface2/40 px-4 py-3">
      <div className="grid gap-3 sm:grid-cols-2">
        <FieldBox label="Saindo do plantão">
          <div className="h-[38px] flex items-center rounded-md border border-jf-border bg-jf-surface px-3 text-sm text-white">
            {isAbertura ? <span className="text-jf-muted italic">— sem passagem —</span> : myName}
          </div>
        </FieldBox>
        <FieldBox label="Entrando no plantão">
          {isAbertura ? (
            // Sem dropdown: quem abre é quem assume. Poder escolher outra pessoa
            // aqui é literalmente o defeito do GUPM-272.
            <div className="h-[38px] flex items-center rounded-md border border-jf-border bg-jf-surface px-3 text-sm text-white">
              {myName}
            </div>
          ) : (
            <select
              value={operatorInId}
              onChange={(e) => onOperatorInChange(e.target.value)}
              className={`h-[38px] w-full appearance-none rounded-md border bg-jf-surface bg-[length:14px_14px] bg-[right_0.6rem_center] bg-no-repeat pl-3 pr-9 text-sm text-white outline-none focus:border-jf-yellow ${
                submitAttempted && !operatorInId ? 'border-red-500' : 'border-jf-border'
              }`}
              style={{
                backgroundImage:
                  "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20' fill='%23FECB00'><path fill-rule='evenodd' d='M5.23 7.21a.75.75 0 011.06.02L10 11.06l3.71-3.83a.75.75 0 111.08 1.04l-4.25 4.39a.75.75 0 01-1.08 0L5.21 8.27a.75.75 0 01.02-1.06z' clip-rule='evenodd'/></svg>\")",
              }}
              disabled={operatorsLoading}
            >
              <option value="">— selecione —</option>
              {operators.map((op) => (
                // Sem autenticador cadastrado a troca é impossível: ela exige
                // dois TOTPs, e o do entrante não existe. Desabilitar aqui move
                // a descoberta para ANTES dos nove itens do checklist — hoje ela
                // vem depois, como `no_totp_set` da Edge Function, quando o
                // operador já preencheu tudo. Em produção isto vale para 4 dos
                // 9 operadores. GUPM-273.
                <option key={op.id} value={op.id} disabled={!op.hasTotp}>
                  {op.name} ({op.profile})
                  {op.hasTotp ? '' : ' — sem autenticador'}
                </option>
              ))}
              {/* Sair do plantão sem rendição é uma escolha DENTRO da mesma
                  lista, não um botão à parte: as duas saídas do turno passam
                  pelo mesmo checklist. GUPM-272 Rev.2. */}
              <option value={SEM_SUBSTITUTO}>— Ninguém — vou encerrar o plantão</option>
            </select>
          )}
        </FieldBox>
      </div>
    </section>
  );
}

export function HandoverStepper({
  itemsCompleted,
  totalItems,
  obsRequired,
  obsOk,
  divergentLabel,
}: {
  itemsCompleted: number;
  totalItems: number;
  obsRequired: boolean;
  obsOk: boolean;
  divergentLabel: string;
}) {
  const progressPct = totalItems > 0 ? (itemsCompleted / totalItems) * 100 : 0;
  // GUPM-273: sem checklist (abertura de plantão) não há progresso a mostrar.
  // "0 de 0 itens" com barra cheia diria que algo foi respondido.
  const temChecklist = totalItems > 0;
  return (
    <section className="flex items-center gap-3 border-b border-jf-border px-4 py-2">
      {temChecklist && (
        <>
          <span className="text-xs font-bold text-jf-dim tabular-nums whitespace-nowrap">
            {itemsCompleted} de {totalItems} itens
          </span>
          <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-jf-border">
            <div
              className={`h-full transition-all ${
                itemsCompleted === totalItems ? 'bg-green-500' : 'bg-jf-yellow'
              }`}
              style={{ width: `${progressPct}%` }}
            />
          </div>
        </>
      )}
      {!temChecklist && (
        <span className="flex-1 text-xs text-jf-muted">
          Descreva abaixo por que está assumindo o plantão sozinho.
        </span>
      )}
      {obsRequired && (
        <span
          title={divergentLabel || 'Observação obrigatória na abertura de plantão'}
          className={`text-[10px] font-bold uppercase whitespace-nowrap ${
            obsOk ? 'text-green-400' : 'text-orange-400'
          }`}
        >
          {obsOk ? '✓ obs ok' : '⚠ obs obrigatória'}
        </span>
      )}
    </section>
  );
}

export interface ChecklistGroup {
  order: number;
  label: string;
  color: GroupColor;
  items: ChecklistItem[];
}

export function HandoverChecklist({
  groups,
  responses,
  onChange,
  submitAttempted,
}: {
  groups: ChecklistGroup[];
  responses: Record<string, ChecklistResponse>;
  onChange: (r: ChecklistResponse) => void;
  submitAttempted: boolean;
}) {
  return (
    <>
      {groups.map((group) => (
        <section key={group.order} className="space-y-2">
          <header className="flex items-center gap-2">
            <span
              className={`inline-block h-2 w-2 rounded-full ${GROUP_COLOR_CLASSES[group.color].dot}`}
            />
            <span
              className={`text-[11px] font-bold uppercase tracking-wider ${GROUP_COLOR_CLASSES[group.color].text}`}
            >
              {group.label}
            </span>
          </header>
          <div className="space-y-2">
            {group.items.map((item) => {
              const resp = responses[item.id];
              return (
                <ChecklistItemRow
                  key={item.id}
                  item={item}
                  response={resp}
                  onChange={onChange}
                  highlightIncomplete={submitAttempted && !isItemComplete(item, resp)}
                />
              );
            })}
          </div>
        </section>
      ))}
    </>
  );
}

export function HandoverObservacao({
  isAbertura,
  isEncerramento,
  obsRequired,
  obsPorDivergencia,
  obsOk,
  submitAttempted,
  divergentLabel,
  divergentIndexes,
  value,
  onChange,
}: {
  isAbertura: boolean;
  isEncerramento: boolean;
  obsRequired: boolean;
  obsPorDivergencia: boolean;
  obsOk: boolean;
  submitAttempted: boolean;
  divergentLabel: string;
  divergentIndexes: number[];
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <>
      {obsPorDivergencia && (
        <div
          role="status"
          className="rounded-lg border border-orange-400/50 bg-orange-500/10 px-3 py-2 text-xs text-orange-200"
        >
          ⚠️ <strong>{divergentLabel}.</strong> Observação obrigatória — descreva o que motivou a
          divergência e as ações tomadas / a tomar.
        </div>
      )}

      <section
        className={`rounded-lg border p-3 transition ${
          obsRequired && !obsOk && submitAttempted
            ? 'border-red-500 bg-red-500/5'
            : 'border-jf-border bg-jf-surface2'
        }`}
      >
        <label className="block text-xs uppercase tracking-wide text-jf-dim mb-2">
          Observação{' '}
          <span className={obsRequired ? 'text-orange-400' : 'text-jf-muted'}>
            {obsRequired ? '— obrigatória' : '(opcional)'}
          </span>
        </label>
        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={
            isAbertura
              ? 'Descreva por que está assumindo o plantão sem passagem (ex.: cheguei e não havia plantonista)…'
              : isEncerramento
              ? 'Descreva por que está encerrando o plantão sem rendição (ex.: fim do expediente e ninguém chegou)…'
              : obsPorDivergencia
              ? `Explique o que ocorreu no(s) item(ns) ${divergentIndexes.join(', ')} e ações tomadas…`
              : 'Notas adicionais sobre o turno (opcional)…'
          }
          rows={obsRequired ? 4 : 2}
          maxLength={2000}
          className="w-full resize-y rounded-md border border-jf-border bg-jf-surface px-3 py-2 text-sm text-white outline-none focus:border-jf-yellow"
        />
      </section>
    </>
  );
}

export function HandoverTotp({
  umCodigoSo,
  myName,
  entranteName,
  meuCodigo,
  onMeuCodigoChange,
  entranteCode,
  onEntranteCodeChange,
}: {
  /** Abertura e encerramento pedem só o código de quem preenche — não há outra parte. */
  umCodigoSo: boolean;
  myName: string;
  entranteName: string;
  meuCodigo: string;
  onMeuCodigoChange: (v: string) => void;
  entranteCode: string;
  onEntranteCodeChange: (v: string) => void;
}) {
  return (
    <section className="rounded-lg border border-jf-yellow/30 bg-jf-yellow/[0.04] p-4">
      <h3 className="mb-1 text-xs font-bold uppercase tracking-wide text-white">
        🔐 Confirmação de identidade (Microsoft Authenticator)
      </h3>
      <p className="mb-3 text-[11px] text-jf-muted">
        {umCodigoSo
          ? 'Abra seu app autenticador, localize "GeoGuard UPM" e digite o código de 6 dígitos atual. Funciona offline.'
          : 'Cada operador abre seu app autenticador, localiza "GeoGuard UPM" e digita o código de 6 dígitos atual. Funciona offline. Entrante não precisa estar logado.'}
      </p>
      <div className={`grid gap-3 ${umCodigoSo ? '' : 'sm:grid-cols-2'}`}>
        <FieldBox
          label={
            <>
              {umCodigoSo ? 'Seu código' : 'Código do saindo'}
              <span className="ml-1 normal-case tracking-normal text-jf-muted">· {myName}</span>
            </>
          }
        >
          <CodeInput
            value={meuCodigo}
            onChange={onMeuCodigoChange}
            ariaLabel={umCodigoSo ? 'Seu código de confirmação' : 'Código do saindo'}
          />
        </FieldBox>
        {/* Sem segundo código quando não há outra parte: na abertura ninguém
            passa, no encerramento ninguém recebe. */}
        {!umCodigoSo && (
          <FieldBox
            label={
              <>
                Código do entrante
                <span className="ml-1 normal-case tracking-normal text-jf-muted">
                  {entranteName ? `· ${entranteName}` : <span className="italic">· selecione acima</span>}
                </span>
              </>
            }
          >
            <CodeInput
              value={entranteCode}
              onChange={onEntranteCodeChange}
              ariaLabel="Código do entrante"
            />
          </FieldBox>
        )}
      </div>
    </section>
  );
}
